import EmptyState from '@aurora/shared-client/components/common/EmptyState/EmptyState';
import { IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import { ListVariant } from '@aurora/shared-client/components/common/List/enums';
import { PagerVariant } from '@aurora/shared-client/components/common/Pager/enums';
import { PanelType } from '@aurora/shared-client/components/common/Panel/enums';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import TextVariantContext from '@aurora/shared-client/components/context/TextVariantContext';
import useDevice from '@aurora/shared-client/components/useDevice';
import useRegistrationStatus from '@aurora/shared-client/components/users/useRegistrationStatus';
import type { TagPagesAndParams } from '@aurora/shared-client/routes/endUserRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import type {
  Board,
  MessageConstraints
} from '@aurora/shared-generated/types/graphql-schema-types';
import {
  ConversationStyle,
  SortDirection
} from '@aurora/shared-generated/types/graphql-schema-types';
import { NodeType } from '@aurora/shared-types/nodes/enums';
import {
  EndUserComponent,
  EndUserPathParams,
  EndUserQueryParams
} from '@aurora/shared-types/pages/enums';
import { TextAlignment } from '@aurora/shared-types/texts/enums';
import { getAsEnum } from '@aurora/shared-utils/helpers/objects/EnumHelper';
import {
  deepClone,
  merge,
  UndefinedValueMergeBehavior
} from '@aurora/shared-utils/helpers/objects/ObjectHelper';
import { getLog } from '@aurora/shared-utils/log';
import React, { useCallback, useContext, useEffect } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import MessageListWidgetHelper from '../../../helpers/messages/MessageHelper/MessageListWidgetHelper';
import { ItemType, MessageTimestamp, MessageViewVariant } from '../../../types/enums';
import type {
  MessageViewsForWidgetQuery,
  MessageViewsForWidgetQueryVariables,
  MessageViewsForWidgetFragment
} from '../../../types/graphql-types';
import PaneledItemList from '../../common/List/PaneledItemList/PaneledItemList';
import type { WidgetFC } from '../../common/Widget/types';
import EditableWidget from '../../common/Widget/EditableWidget';
import type { ItemViewTypeAndProps } from '../../entities/types';
import MessagesWidgetFilters from '../../common/MessagesWidgetFilters/MessagesWidgetFilters';
import useEntityViewQuery from '../../useEntityViewQuery';
import useQueryParamArrayForWidget from '../../useQueryParamArrayForWidget';
import useQueryParamValueForWidget from '../../useQueryParamValueForWidget';
import useTranslation from '../../useTranslation';
import MessageListTabs, {
  MessageListTabItem,
  messageListTabItemMap
} from '../MessageListTabs/MessageListTabs';
import messagesQuery from '../MessageViewsForWidget.query.graphql';
import type {
  MessageListWidgetExtendedProps,
  MessageSortByOption,
  MessageTabOptions
} from '../types';
import { WidgetType } from '../types';
import PaneledItemListSubHeader from '../../common/List/PaneledItemListSubHeader/PaneledItemListSubHeader';
import type { ContextNodeFragment } from '@aurora/shared-generated/types/graphql-types';
import { buildMessagesConstraintsForNodeById } from '@aurora/shared-client/helpers/nodes/NodeHelper';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import useSocialFeedFeatures from '../../../helpers/socialfeed/useSocialFeedFeatures';
import useSocialFeedPaginationEnabled from '../../../helpers/socialfeed/useSocialFeedPaginationEnabled';

const log = getLog(module);

const sortByFieldOptions: MessageSortByOption[] = [
  {
    key: 'recentActivity',
    value: {
      conversationLastPostingActivityTime: {
        direction: SortDirection.Desc
      }
    }
  },
  {
    key: 'recentActivityForUser',
    value: {
      lastPublishTime: {
        direction: SortDirection.Desc
      }
    }
  },
  {
    key: 'newest',
    value: {
      postTime: {
        direction: SortDirection.Desc
      }
    }
  }
];

const tabOptions: MessageTabOptions = {
  default: {
    [MessageListTabItem.MOST_RECENT]: true,
    [MessageListTabItem.MOST_RECENT_USER_CONTENT]: true,
    [MessageListTabItem.NEWEST]: false
  },
  additional: {
    [MessageListTabItem.TRENDING]: false,
    [MessageListTabItem.MOST_VIEWED]: true,
    [MessageListTabItem.MOST_REPLIES]: false,
    [MessageListTabItem.MOST_KUDOED]: false,
    [MessageListTabItem.SAVED]: false,
    [MessageListTabItem.NO_REPLIES]: false,
    [MessageListTabItem.NO_SOLUTIONS]: false,
    [MessageListTabItem.SOLUTIONS]: false,
    [MessageListTabItem.UNDER_REVIEW]: false,
    [MessageListTabItem.REJECTED]: false
  }
};

function getDefaultProps(context: ContextNodeFragment): MessageListWidgetExtendedProps {
  return {
    style: 'list',
    viewVariant: {
      type: MessageViewVariant.INLINE,
      props: {
        ...MessageListWidgetHelper.getMessageViewInlineDefaults(),
        ...MessageListWidgetHelper.getMessageViewCommonDefaults(),
        timeStampType: MessageTimestamp.LAST_POSTING_ACTIVITY_TIME
      }
    },
    listVariant: {
      type: ListVariant.LIST_GROUP
    },
    pagerVariant: {
      type: PagerVariant.LOAD_MORE
    },
    pageSize: 10,
    useTitle: true,
    hideIfEmpty: true,
    pagerOption: true,
    pagerOptionCard: false,
    panelType: PanelType.DIVIDER,
    sortByFieldValues: sortByFieldOptions,
    conversationStyle: null,
    showTabs: false,
    addTags: false,
    tabItemMap: deepClone(tabOptions),
    nodeScopeId: context.id
  };
}

export function getFinalProps(
  props: MessageListWidgetExtendedProps,
  hasContextUser: boolean,
  nodeContext: ContextNodeFragment
): MessageListWidgetExtendedProps {
  const defaultProps = getDefaultProps(nodeContext);
  if (hasContextUser && defaultProps?.tabItemMap) {
    // Deep clone before mutation to avoid modifying shared reference
    defaultProps.tabItemMap = deepClone(defaultProps.tabItemMap);
    defaultProps.tabItemMap.additional[MessageListTabItem.MOST_KUDOED] = true;
  }
  const finalProps: MessageListWidgetExtendedProps = merge(defaultProps, props, {
    undefinedMergeBehavior: UndefinedValueMergeBehavior.IGNORE_BEFORE_MERGE,
    mergeNested: false
  });

  // Deep clone tabItemMap to avoid mutating shared references
  if (finalProps.tabItemMap) {
    finalProps.tabItemMap = deepClone(finalProps.tabItemMap);
  }

  // Enable moderation tabs and polls tab for user profile pages (after merge to avoid mutating props)
  if (hasContextUser && finalProps?.tabItemMap?.additional) {
    finalProps.tabItemMap.additional[MessageListTabItem.UNDER_REVIEW] = true;
    finalProps.tabItemMap.additional[MessageListTabItem.REJECTED] = true;
    // SAVED tab removed from profile — now lives on /following page (KCOMM-650)
    finalProps.tabItemMap.additional[MessageListTabItem.POLLS] = true;
  }
  if (!finalProps.sorts) {
    finalProps.sorts = hasContextUser
      ? {
          lastPublishTime: {
            direction: SortDirection.Desc
          }
        }
      : {
          conversationLastPostingActivityTime: {
            direction: SortDirection.Desc
          }
        };
  }
  return finalProps;
}

/**
 * A node widget that displays messages of a given node focusing on recent activity.
 *
 * @constructor
 *
 * @author Sravan Reddy
 */
const MessageListForNodeByRecentActivityWidget: WidgetFC<
  MessageListWidgetExtendedProps
> = props => {
  const { isVisible, ...rest } = props;
  const { contextNode, contextUser, authUser } = useContext(AppContext);
  const finalProps = getFinalProps(rest, !!contextUser, contextNode);

  const {
    titleContextVariant,
    sorts,
    style,
    viewVariant,
    listVariant,
    pagerVariant,
    className,
    pageSize,
    useTitle,
    hideIfEmpty,
    panelType,
    sortByFieldValues,
    conversationStyle,
    showTabs,
    addTags,
    tabItemMap,
    nodeScopeId
  } = finalProps;

  const cx = useClassNameMapper();

  const user = contextUser ?? authUser;

  const tenantContext = useContext(TenantContext);

  const { key: tagsParameterKey, value: tagsParameterValue } = useQueryParamArrayForWidget(
    EndUserQueryParams.SEARCH_BY_TAGS_FILTER,
    []
  );

  const { router } = useEndUserRoutes();
  const tagNamePathParameter = router.getPathParam<TagPagesAndParams>(EndUserPathParams.TAG_NAME);
  const tagsValue =
    tagsParameterValue.length > 0
      ? tagsParameterValue
      : tagNamePathParameter === null
      ? []
      : [tagNamePathParameter];

  let constraints: MessageConstraints = buildMessagesConstraintsForNodeById(
    tenantContext,
    nodeScopeId,
    tagsValue
  );

  const { isAnonymous } = useRegistrationStatus();

  // Check if current user can view moderation tabs (Under Review & Rejected)
  const canViewModerationTabs = (): boolean => {
    // Must have both contextUser (profile being viewed) and authUser (logged in user)
    if (!contextUser || !authUser) {
      return false;
    }

    // User viewing their own profile can see their moderation tabs
    return authUser.id === contextUser.id;
  };

  const defaultTabs = {};
  const additionalTabs = {};

  // Use tabItemMap keys (from runtime props) instead of hardcoded tabs constant
  // This ensures we only process tabs that are actually defined in the config
  const defaultTabKeys = Object.keys(tabItemMap.default || {});
  const additionalTabKeys = Object.keys(tabItemMap.additional || {});

  defaultTabKeys.forEach(value => {
    defaultTabs[value] = tabItemMap.default[value];
  });

  // Polls tab is visible when the feature is enabled
  const pollFeatureEnabled = tenantContext?.publicConfig?.enablePolls === true;

  additionalTabKeys.forEach(value => {
    // Filter out moderation tabs if user doesn't have permission
    if (
      (value === MessageListTabItem.UNDER_REVIEW || value === MessageListTabItem.REJECTED) &&
      !canViewModerationTabs()
    ) {
      return; // Skip these tabs
    }
    // SAVED tab removed from profile — now lives on /following page (KCOMM-650)
    if (contextUser && value === MessageListTabItem.SAVED) {
      return;
    }
    // Filter out polls tab if feature is disabled
    if (value === MessageListTabItem.POLLS && !pollFeatureEnabled) {
      return;
    }
    additionalTabs[value] = tabItemMap.additional[value];
  });

  const tabItemOptions = { default: defaultTabs, additional: additionalTabs };
  const { isMobile } = useDevice();
  if (contextUser) {
    constraints.authorId = { eq: user.id };
  } else {
    constraints.depth = { eq: 0 };
  }
  let contextBoard: Board | undefined;
  const adjustedViewVariant = deepClone(viewVariant);
  if (conversationStyle) {
    constraints.conversationStyle = {
      eq: getAsEnum(conversationStyle, ConversationStyle)
    };
  } else if (contextNode?.nodeType === NodeType.BOARD) {
    adjustedViewVariant.props.useNodeHoverCard = false;
    contextBoard = contextNode as Board;
    constraints.conversationStyle = { eq: contextBoard.conversationStyle };
  }

  const configProps: MessageListWidgetExtendedProps = {
    sorts: sorts,
    style,
    viewVariant: adjustedViewVariant,
    listVariant,
    pagerVariant,
    className,
    pageSize,
    useTitle,
    hideIfEmpty,
    panelType,
    sortByFieldValues,
    conversationStyle,
    showTabs,
    tabItemMap: tabItemOptions,
    titleContextVariant
  };

  const messageWidgetConfigHelper = new MessageListWidgetHelper(
    configProps,
    contextBoard,
    contextUser,
    WidgetType.RECENT,
    null,
    false,
    contextNode
  );

  const tabList = messageWidgetConfigHelper.fetchTabs();
  let [firstTab] = tabList;

  if (firstTab === undefined && addTags) {
    firstTab = 'tagsFilter' as MessageListTabItem;
  }

  const { key: queryParamKey, value: queryParamValue } = useQueryParamValueForWidget(
    'tab',
    firstTab
  );
  const requestedTab = getAsEnum<typeof MessageListTabItem>(
    queryParamValue,
    MessageListTabItem,
    firstTab
  );
  const activeTab = tabList.includes(requestedTab) ? requestedTab : firstTab;

  let sortForWidget = sorts;
  if (showTabs && activeTab) {
    const selectedTab = messageListTabItemMap[activeTab];
    if (selectedTab) {
      if (activeTab === MessageListTabItem.SOLUTIONS) {
        if (contextUser) {
          constraints.solution = { eq: true };
        } else {
          constraints.conversationSolved = { eq: true };
        }
      }
      sortForWidget = selectedTab.sorts;
      constraints = { ...constraints, ...selectedTab.constraints };
      if (adjustedViewVariant.props.timeStampType) {
        if (sortForWidget.conversationLastPostingActivityTime) {
          adjustedViewVariant.props.timeStampType = MessageTimestamp.LAST_POSTING_ACTIVITY_TIME;
        } else if (sortForWidget.lastPublishTime) {
          adjustedViewVariant.props.timeStampType = MessageTimestamp.LAST_PUBLISHED_TIME;
        } else {
          adjustedViewVariant.props.timeStampType = MessageTimestamp.POST_TIME;
        }
      }
    } else {
      log.warn('No tab %s found in tabList %O', activeTab, Object.keys(messageListTabItemMap));
    }
  }

  const { feedEnabled: isSocialFeedEnabled } = useSocialFeedFeatures(
    contextBoard?.displayId,
    contextBoard?.conversationStyle === ConversationStyle.Idea
  );
  const isSocialFeedPaginationEnabled = useSocialFeedPaginationEnabled();

  const variables = {
    constraints,
    sorts: sortForWidget,
    first: pageSize,
    useUnreadCount: !isAnonymous,
    useFeedBodyTruncation: isSocialFeedEnabled,
    useUnreadHighlighting: isSocialFeedEnabled && !isAnonymous,
    useSocialFeedPagination: isSocialFeedPaginationEnabled
  };

  const queryResult = useEntityViewQuery<
    ItemType.MESSAGE,
    MessageViewsForWidgetQuery,
    MessageViewsForWidgetQueryVariables
  >(module, ItemType.MESSAGE, viewVariant, messagesQuery, {
    variables
  });

  const showEmptyState = !hideIfEmpty || tabList.length > 1;
  const { data, loading } = queryResult;

  const textVariantContext = useContext(TextVariantContext);

  useEffect(() => {
    if (!showEmptyState && !loading) {
      isVisible(data?.messages?.edges?.length !== 0);
    }
  }, [data, isVisible, loading, showEmptyState]);

  /**
   * A Predicate to ensure the view variant type.
   */
  function isInline(
    messageViewVariant: ItemViewTypeAndProps<ItemType.MESSAGE, MessageViewVariant>
  ): messageViewVariant is ItemViewTypeAndProps<ItemType.MESSAGE, MessageViewVariant.INLINE> {
    return messageViewVariant.type === MessageViewVariant.INLINE;
  }

  // to force hide/show the elements in compact view.
  if (isInline(adjustedViewVariant)) {
    if (style === 'compact') {
      adjustedViewVariant.props.useBody = false;
      adjustedViewVariant.props.useCompactSpacing = true;
      adjustedViewVariant.props.useAuthorRank = false;
      adjustedViewVariant.props.useKudosCount = false;
      adjustedViewVariant.props.usePreviewMedia = false;
      adjustedViewVariant.props.useTags = false;
      adjustedViewVariant.props.useRepliesCount = false;
      adjustedViewVariant.props.useMedia = false;
      adjustedViewVariant.props.useNode = false;
      adjustedViewVariant.props.useTimeToRead = false;
      adjustedViewVariant.props.useUnreadCount = false;
      adjustedViewVariant.props.useViewCount = false;
      adjustedViewVariant.props.avatarSize = IconSize.PX_40;
      adjustedViewVariant.props.boardIconSize = IconSize.PX_24;
      adjustedViewVariant.props.renderPostTimeBeforeAuthor = true;
    } else {
      adjustedViewVariant.props.renderPostTimeBeforeAuthor = false;
    }
  }

  // FEED-1.1: Enable body truncation for social feed (3000 char limit)
  if (isSocialFeedEnabled) {
    adjustedViewVariant.props.useFeedBodyTruncation = true;
  }

  if (isMobile) {
    adjustedViewVariant.props.boardIconSize = IconSize.PX_24;
  }

  const localTextVariantContext = MessageListWidgetHelper.getTextVariantForMessageQueryVariables(
    variables,
    textVariantContext,
    user
  );
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.MESSAGE_LIST_FOR_NODE_BY_RECENT_ACTIVITY_WIDGET,
    false,
    localTextVariantContext
  );

  /**
   * A callback function to render Empty State when entity list has no messages
   */
  const renderNoPosts = useCallback((): React.ReactElement => {
    return (
      <EmptyState description={formatMessage('emptyDescription')} alignment={TextAlignment.LEFT} />
    );
  }, [formatMessage]);

  function renderHeader(): React.ReactElement {
    return <h3 className={cx('text-break h5 mb-0')}>{formatMessage('title')}</h3>;
  }

  if (textLoading) {
    return null;
  }

  /**
   * Function to render tabbed filters
   */
  function renderTabList(): React.ReactElement {
    return (
      <PaneledItemListSubHeader panel={panelType} as={'nav'}>
        <MessageListTabs
          activeTab={activeTab}
          items={tabList}
          queryParamKey={queryParamKey}
          recordHistory={false}
          conversationStyle={configProps.conversationStyle as ConversationStyle}
          useBottomBorder={panelType === PanelType.STANDARD}
        />
      </PaneledItemListSubHeader>
    );
  }

  /**
   * Function to render filters
   */
  const RenderFilterBar: React.FC = () => {
    return (
      <PaneledItemListSubHeader
        panel={panelType}
        as={'nav'}
        className={cx({ 'lia-bubble': panelType === PanelType.BUBBLE })}
      >
        <MessagesWidgetFilters
          tagsParameterKey={tagsParameterKey}
          selectedTags={tagsParameterValue}
          nodeId={nodeScopeId}
          onChange={() => {}}
          className={cx(
            { 'lia-g-py-10': panelType !== PanelType.BUBBLE },
            { 'lia-g-pt-0': panelType === PanelType.BUBBLE }
          )}
        />
      </PaneledItemListSubHeader>
    );
  };

  /**
   * Function to render filters and sorts tabs list
   */
  const RenderSubHeaders: React.FC = () => {
    return (
      <>
        {showTabs && renderTabList()}
        {addTags && style === 'list' && <RenderFilterBar />}
      </>
    );
  };

  const useSubHeader =
    activeTab && (queryResult.data?.messages?.edges?.length > 0 || showEmptyState);

  return (
    <TextVariantContext.Provider value={localTextVariantContext}>
      <EditableWidget<MessageListWidgetExtendedProps> props={finalProps}>
        <PaneledItemList<
          MessageViewsForWidgetFragment,
          ItemType.MESSAGE,
          ItemViewTypeAndProps<ItemType.MESSAGE, MessageViewVariant>,
          MessageViewsForWidgetQuery,
          MessageViewsForWidgetQueryVariables
        >
          type={ItemType.MESSAGE}
          variant={adjustedViewVariant}
          listVariant={listVariant}
          queryResult={queryResult}
          panel={panelType}
          pagerVariant={pagerVariant}
          pageSize={pageSize}
          header={useTitle && renderHeader}
          subHeader={useSubHeader && RenderSubHeaders}
          bodyClassName="lia-g-mb-0"
          className={cx(className)}
          empty={renderNoPosts}
          useEmpty={showEmptyState}
          footerClassName={cx({ 'lia-g-mt-5 bg-transparent': style === 'card' })}
          useFooter={pagerVariant.type !== PagerVariant.NONE}
          useInfiniteScroll={isSocialFeedPaginationEnabled}
          loadMoreLabel={formatMessage('loadMorePosts')}
          endOfFeedLabel={formatMessage('endOfFeed')}
          id="MessageListForNodeByRecentActivityWidget"
          role="tabpanel"
          showHeadersWhileLoading
        />
      </EditableWidget>
    </TextVariantContext.Provider>
  );
};

export default MessageListForNodeByRecentActivityWidget;
